import { DateTimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { Box, CircularProgress, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { getUIAlert, setUIAlert, deleteUIAlert } from "../../redux/reducer/ui";
import globalStyles from "../../globalStyles";
import Button from "../atoms/Button";
import { UIAlert } from "../../interface/message";

interface UIAlertManagerParams {
}

const UIAlertManager = ({
}: UIAlertManagerParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const uiAlert = useSelector<RootState, UIAlert | undefined>(({ ui }) => ui.uiAlert);
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['ui/GET_UI_ALERT']);
    const setLoading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['ui/SET_UI_ALERT']);
    const deleteLoading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['ui/DELETE_UI_ALERT']);

    const [title, setTitle] = useState<string>("");
    const [message, setMessage] = useState<string>("");
    const [startDate, setStartDate] = useState<DateTime | null>(null);
    const [endDate, setEndDate] = useState<DateTime | null>(null);
    const [startTimeOption, setStartTimeOption] = useState<'start-of-day' | 'end-of-day' | 'custom'>('start-of-day');
    const [endTimeOption, setEndTimeOption] = useState<'start-of-day' | 'end-of-day' | 'custom'>('end-of-day');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);

    useEffect(() => {
        dispatch(getUIAlert({}));
    }, []);

    useEffect(() => {
        if (uiAlert) {
            setTitle(uiAlert.title);
            setMessage(uiAlert.message);
            setStartDate(DateTime.fromISO(uiAlert.startDate));
            setEndDate(DateTime.fromISO(uiAlert.endDate));
        }
    }, [uiAlert]);

    const handleSubmit = () => {
        if (!title || !message || !startDate || !endDate) return;

        let finalStartDate = startDate;
        let finalEndDate = endDate;

        if (startTimeOption === 'start-of-day') {
            finalStartDate = startDate.startOf('day');
        } else if (startTimeOption === 'end-of-day') {
            finalStartDate = startDate.endOf('day');
        }

        if (endTimeOption === 'start-of-day') {
            finalEndDate = endDate.startOf('day');
        } else if (endTimeOption === 'end-of-day') {
            finalEndDate = endDate.endOf('day');
        }

        dispatch(setUIAlert({
            input: {
                title,
                message,
                startDate: finalStartDate.toISO() || '',
                endDate: finalEndDate.toISO() || '',
            },
        }));
    };

    const handleClearConfirm = () => {
        dispatch(deleteUIAlert({
            input: {},
            onSuccess: () => {
                setTitle("");
                setMessage("");
                setStartDate(null);
                setEndDate(null);
                setStartTimeOption('start-of-day');
                setEndTimeOption('end-of-day');
                setConfirmDialogOpen(false);
            },
        }));
    };

    return (
        <Box>
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the current UI alert? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleClearConfirm} variant="contained" color="error" loading={deleteLoading}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            {
                loading ? <CircularProgress /> : (
                    <Box sx={[globalStyles.col, { gap: 3 }]}>
                        <TextField
                            label="Alert Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Alert Message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            required
                        />
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                                <DateTimePicker
                                    label="Start Date"
                                    value={startDate}
                                    onChange={(newValue: DateTime | null) => {
                                        setStartDate(newValue);
                                        if (newValue) setStartTimeOption('custom');
                                    }}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                                <FormControl component="fieldset" sx={{ mt: 1 }}>
                                    <FormLabel component="legend">Start Time</FormLabel>
                                    <RadioGroup
                                        value={startTimeOption}
                                        onChange={(e) => {
                                            const option = e.target.value as 'start-of-day' | 'end-of-day' | 'custom';
                                            setStartTimeOption(option);
                                            if (startDate && option !== 'custom') {
                                                if (option === 'start-of-day') {
                                                    setStartDate(startDate.startOf('day'));
                                                } else {
                                                    setStartDate(startDate.endOf('day'));
                                                }
                                            }
                                        }}
                                    >
                                        <FormControlLabel value="start-of-day" control={<Radio />} label="Start of Day" />
                                        <FormControlLabel value="end-of-day" control={<Radio />} label="End of Day" />
                                        <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <DateTimePicker
                                    label="End Date"
                                    value={endDate}
                                    onChange={(newValue: DateTime | null) => {
                                        setEndDate(newValue);
                                        if (newValue) setEndTimeOption('custom');
                                    }}
                                    slotProps={{ textField: { fullWidth: true, required: true } }}
                                />
                                <FormControl component="fieldset" sx={{ mt: 1 }}>
                                    <FormLabel component="legend">End Time</FormLabel>
                                    <RadioGroup
                                        value={endTimeOption}
                                        onChange={(e) => {
                                            const option = e.target.value as 'start-of-day' | 'end-of-day' | 'custom';
                                            setEndTimeOption(option);
                                            if (endDate && option !== 'custom') {
                                                if (option === 'start-of-day') {
                                                    setEndDate(endDate.startOf('day'));
                                                } else {
                                                    setEndDate(endDate.endOf('day'));
                                                }
                                            }
                                        }}
                                    >
                                        <FormControlLabel value="start-of-day" control={<Radio />} label="Start of Day" />
                                        <FormControlLabel value="end-of-day" control={<Radio />} label="End of Day" />
                                        <FormControlLabel value="custom" control={<Radio />} label="Custom" />
                                    </RadioGroup>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="contained"
                                loading={setLoading}
                                onClick={handleSubmit}
                                disabled={!title || !message || !startDate || !endDate}
                            >
                                Set UI Alert
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setConfirmDialogOpen(true)}
                            >
                                Clear Alert
                            </Button>
                        </Box>
                    </Box>
                )
            }
        </Box>
    );
};

export default UIAlertManager;
